/**
 * Roadmap Configuration
 *
 * Edit this file to update the roadmap content displayed on the public roadmap page.
 *
 * Status options: 'completed' | 'in-progress' | 'planned' | 'considering'
 * Quarter format: 'Q1 2025', 'Q2 2025', etc.
 */

export interface RoadmapItem {
  id: string
  title: string
  description: string
  status: 'completed' | 'in-progress' | 'planned' | 'considering'
  quarter?: string
  category: 'feature' | 'integration' | 'infrastructure' | 'database'
}

export interface RoadmapSection {
  title: string
  description: string
  items: RoadmapItem[]
}

export const roadmapConfig: RoadmapSection[] = [
  {
    title: 'Recently Completed',
    description: 'Features and improvements we have shipped',
    items: [
      {
        id: 'ei-312',
        title: 'Ecoinvent 3.12 Database',
        description:
          'Added support for the latest Ecoinvent 3.12 database with updated LCI data and improved coverage.',
        status: 'completed',
        quarter: 'Q4 2025',
        category: 'database',
      },
      {
        id: 'mcp-integration',
        title: 'MCP Server Integration',
        description:
          'Full Model Context Protocol support for seamless AI assistant integration with Claude, ChatGPT, and VS Code.',
        status: 'completed',
        quarter: 'Q4 2025',
        category: 'integration',
      },
      {
        id: 'custom-lca',
        title: 'Custom LCA Projects',
        description:
          'Create and manage custom LCA projects with your own activities and exchanges.',
        status: 'completed',
        quarter: 'Q3 2025',
        category: 'feature',
      },
    ],
  },
  {
    title: 'In Progress',
    description: 'What we are currently working on',
    items: [
      {
        id: 'batch-calculations',
        title: 'Batch LCA Calculations',
        description:
          'Run multiple LCA calculations in parallel for faster analysis of product portfolios.',
        status: 'in-progress',
        quarter: 'Q1 2026',
        category: 'feature',
      },
      {
        id: 'result-visualization',
        title: 'Enhanced Result Visualization',
        description:
          'Interactive charts and graphs for better understanding of LCA results and hotspot analysis.',
        status: 'in-progress',
        quarter: 'Q1 2026',
        category: 'feature',
      },
    ],
  },
  {
    title: 'Planned',
    description: 'Features on our near-term roadmap',
    items: [
      {
        id: 'excel-export',
        title: 'Excel Export & Reporting',
        description:
          'Export LCA results directly to formatted Excel reports for stakeholder communication.',
        status: 'planned',
        quarter: 'Q2 2026',
        category: 'feature',
      },
      {
        id: 'team-collaboration',
        title: 'Team Collaboration',
        description: 'Share projects, collaborate with team members, and manage permissions.',
        status: 'planned',
        quarter: 'Q2 2026',
        category: 'feature',
      },
      {
        id: 'api-v2',
        title: 'API v2 with GraphQL',
        description: 'Next-generation API with GraphQL support for more flexible data queries.',
        status: 'planned',
        quarter: 'Q3 2026',
        category: 'infrastructure',
      },
    ],
  },
  {
    title: 'Under Consideration',
    description: 'Ideas we are exploring based on user feedback',
    items: [
      {
        id: 'consequential-lca',
        title: 'Consequential LCA Support',
        description: 'Support for consequential system models in addition to attributional LCA.',
        status: 'considering',
        category: 'database',
      },
      {
        id: 'uncertainty-analysis',
        title: 'Monte Carlo Uncertainty Analysis',
        description: 'Statistical uncertainty analysis for more robust LCA results.',
        status: 'considering',
        category: 'feature',
      },
      {
        id: 'slack-integration',
        title: 'Slack Integration',
        description: 'Run LCA queries directly from Slack channels.',
        status: 'considering',
        category: 'integration',
      },
    ],
  },
]

// Helper to get status display properties
export const getStatusConfig = (status: RoadmapItem['status']) => {
  const configs = {
    completed: {
      label: 'Completed',
      color: 'bg-green-500/10 text-green-500 border-green-500/20',
      icon: '✓',
    },
    'in-progress': {
      label: 'In Progress',
      color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      icon: '◐',
    },
    planned: {
      label: 'Planned',
      color: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      icon: '○',
    },
    considering: {
      label: 'Considering',
      color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      icon: '?',
    },
  }
  return configs[status]
}

// Helper to get category display properties
export const getCategoryConfig = (category: RoadmapItem['category']) => {
  const configs = {
    feature: {
      label: 'Feature',
      color: 'bg-primary/10 text-primary',
    },
    integration: {
      label: 'Integration',
      color: 'bg-cyan-500/10 text-cyan-500',
    },
    infrastructure: {
      label: 'Infrastructure',
      color: 'bg-slate-500/10 text-slate-500',
    },
    database: {
      label: 'Database',
      color: 'bg-emerald-500/10 text-emerald-500',
    },
  }
  return configs[category]
}
